/**
 * Content-driven shortcut suggestions for a comment — deterministic keyword
 * rules over the note's own text, nothing about the record's state. The
 * thread offers, it never writes: a suggestion is only ever a chip the reader
 * may click.
 */
export type SuggestedActionKey =
    | "log_call"
    | "schedule_meeting"
    | "schedule_follow_up"
    | "create_task"
    | "create_subtask"
    | "request_service"
    | "report_incident";

const ACTIVITY_PARENTS = new Set(["lead", "client", "company", "provider", "mission_request", "invoice", "bill", "payment", "organization"]);
const MISSION_FAMILY = new Set(["mission", "itinerary", "leg"]);

const CALL = /\b(call|phone|ring|dial|callback|call back|voicemail|no answer|whatsapp)\b/i;
const MEETING = /\b(meet|meeting|visit|appointment|sit down|demo|presentation|site survey)\b/i;
const FOLLOW_UP = /\b(follow[- ]?up|remind(er)?|check back|chase|get back|circle back|next week|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|by (the )?\d{1,2}(st|nd|rd|th)?|deadline|due)\b/i;
const TASK = /\b(task|to-?do|need(s)? to|must|should|please|prepare|arrange|book|confirm|send|attach|update|make sure|don't forget|action)\b/i;
const INCIDENT = /\b(incident|accident|attack|injur(y|ed)|threat|suspicious|breach|panic|crash|collision|assault|harass|stalk|crowd|protest)\b/i;
const SERVICE = /\b(extra|additional|another|one more|second|more) (vehicle|car|driver|operator|guard|escort|resource|service|team|unit)s?\b|\brequest(ed)? (a |an )?(service|vehicle|escort|driver)\b/i;

const MAX = 3;

/**
 * Rank suggestions for `text` on a `parentType` thread. Order = strongest
 * signal first; the caller filters by what the reader may actually do.
 */
export function suggestActionsForText(text: string, parentType: string): SuggestedActionKey[] {
    const t = (text ?? "").replace(/@\[([^\]]+)\]\(ref:[^)]+\)/g, "@$1").trim();
    if (t.length < 3) return [];
    const out: SuggestedActionKey[] = [];
    const push = (k: SuggestedActionKey) => { if (out.length < MAX && !out.includes(k)) out.push(k); };
    const isActivityParent = ACTIVITY_PARENTS.has(parentType);
    const isMission = MISSION_FAMILY.has(parentType);

    if (isMission && INCIDENT.test(t)) push("report_incident");
    if (isMission && SERVICE.test(t)) push("request_service");
    if (isActivityParent && CALL.test(t)) push("log_call");
    if (isActivityParent && MEETING.test(t)) push("schedule_meeting");
    if (isActivityParent && FOLLOW_UP.test(t)) push("schedule_follow_up");
    if (TASK.test(t) || (!isActivityParent && FOLLOW_UP.test(t))) push(parentType === "task" ? "create_subtask" : "create_task");
    // A question with no other signal is usually "someone has to do something".
    if (out.length === 0 && /\?\s*$/.test(t) && t.length > 12) push(parentType === "task" ? "create_subtask" : "create_task");
    return out;
}
